import { useState, useEffect } from "react";
import axios from "axios";
import { fetchItems } from "../../utils/fetchItemData";
import { useModalStack } from "../../utils/modalContext";
import { useCurrencies } from "../../hooks/useCurrencies";

export default function ListingsCard({ player }) {
  const [listings, setListings] = useState([]);
  const [itemsData, setItemsData] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [coinsIcon, setCoinsIcon] = useState(null);
  const { data } = useCurrencies();
  const { pushModal } = useModalStack();

  useEffect(() => {
    if (!data) return;

    const coin = data.find((cur) => cur.id === "cur_coins");
    if (coin) {
      setCoinsIcon(coin.image);
    }
  }, [data]);

  const handleOpen = async (itemId) => {
    try {
      const data = await fetchItems({ itemId });
      if (data?.[0]) {
        pushModal({
          id: `item-${itemId}`,
          type: "itemModal",
          props: {
            item: data[0],
            onClose: () => {},
            showNavigation: false,
            onNavigate: null,
            totalItems: 1,
            index: null,
          },
        });
      }
    } catch (err) {
      console.error("Failed to fetch item:", err);
    }
  };

  useEffect(() => {
    setListings([]);
    if (!player) return;

    const fetchData = async () => {
      try {
        // Fetch items first
        const itemsResponse = await axios.get("https://api.pixelore.wiki/api/item-search");
        const itemsMap = Object.fromEntries(itemsResponse.data.map((item) => [item.id, item]));
        setItemsData(itemsMap);
        setListings(player.listings || []);
      } catch (err) {
        console.error("Error fetching data:", err);
        setError("Failed to load listings.");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [player]);

  if (loading) return <p>Loading listings...</p>;
  if (error) return <p className="text-red-500">{error}</p>;
  if (listings.length === 0)
    return (
      <div className="bg-[var(--panel-section-bg)] bg-opacity-80 text-[var(--text-color)]  rounded-2xl shadow-lg p-6 w-full border border-[var(--border-color)]  min-h-[450px]">
        <div className="grid grid-cols-1 gap-4 mt-4 max-h-[350px]">
          <p className="text-[var(--text-color)]  text-center p-4 bg-[var(--panel-sections)] border border-[var(--entity-bg-color)]rounded-lg shadow-sm">
            No Market listings found.
          </p>
        </div>
      </div>
    );

  return (
    <div className="bg-[var(--panel-section-bg)] bg-opacity-80 text-[var(--text-color)]  rounded-2xl shadow-lg p-6 w-full border border-[var(--border-color)]  min-h-[450px] max-h-[450px] transition-transform duration-300 hover:shadow-[0px_0px_10px_2px] hover:shadow-[var(--border-color)] ">
      <h2 className="text-lg font-bold text-center">Market Listings</h2>
      <div className="grid grid-cols-1 gap-2 mt-1 overflow-y-auto max-h-[375px]">
        {loading ? (
          <p>Loading Listings....</p>
        ) : (
          listings.map((listing) => {
            const item = itemsData[listing.itemId];
            return (
              <div
                key={listing._id}
                className="flex items-center p-4 bg-[var(--panel-sections)]  border border-[var(--entity-bg-color)] rounded-lg shadow-sm cursor-pointer"
                onClick={() => handleOpen(listing.itemId)}
              >
                <div className="w-12 h-12 mr-4 rounded-md flex items-center justify-center shrink-0 bg-[var(--entity-bg-color)] hover:brightness-90 transition-[filter]">
                  {item?.image ? (
                    <img
                      src={item.image}
                      alt={item?.name || listing.itemId}
                      className="w-12 h-12 object-contain"
                      onError={(e) => {
                        e.currentTarget.style.display = "none";
                        e.currentTarget.nextSibling.style.display = "block";
                      }}
                    />
                  ) : null}
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="w-6 h-6 text-[var(--text-color)] opacity-40"
                    style={{ display: item?.image ? "none" : "block" }}
                  >
                    <circle cx="12" cy="12" r="10" />
                    <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 2-3 4" />
                    <line x1="12" y1="17" x2="12.01" y2="17" />
                  </svg>
                </div>
                <div>
                  <h3 className="font-semibold text-xs">
                    {item?.name || listing.itemId} {/* Fallback to itemId if name is missing */}
                  </h3>
                  <p className="text-xs flex items-center">
                    Price: {listing.price.toLocaleString()}
                    {coinsIcon && <img src={coinsIcon} alt="$Coins" className="w-5 h-5 ml-2" />}
                  </p>
                  <p className="text-xs">Quantity: {listing.quantity}</p>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
